import { useState, useEffect, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Sun, Moon, Search, Bell, LogOut, User, Settings, Menu, Users, Building2, FileText, ListTodo } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';

export const TopBar = ({ onMobileMenu }) => {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const loadAlerts = async () => {
      try {
        const { data } = await api.get('/dashboard/stats');
        if (data && data.recent_alerts) {
          setAlerts(data.recent_alerts);
        }
      } catch (err) {
        console.error('Failed to load alerts:', err);
      }
    };
    if (user) {
      loadAlerts();
      // Refresh alerts every 30 seconds
      const interval = setInterval(loadAlerts, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const initials = user
    ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()
    : 'VS';

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) { setResults([]); setShowResults(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get('/search', { params: { q: query } });
        setResults(data.results || []);
        setShowResults(true);
      } catch { setResults([]); }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowResults(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const typeIcon = { vendor: Users, property: Building2, document: FileText, task: ListTodo };

  return (
    <header data-testid="app-topbar" className="sticky top-0 z-30 border-b border-border bg-background/70 backdrop-blur supports-[backdrop-filter]:bg-background/50">
      <div className="flex h-16 items-center justify-between gap-4 px-4 sm:px-6">
        {/* Mobile menu */}
        <Button variant="ghost" size="icon" className="lg:hidden h-9 w-9 shrink-0" onClick={onMobileMenu}>
          <Menu className="h-5 w-5" />
        </Button>

        {/* Search */}
        <div className="relative flex-1 max-w-md hidden sm:block" ref={searchRef}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="topbar-search-input"
            placeholder="Search vendors, properties, documents..."
            className="pl-9 bg-card border-border"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => results.length > 0 && setShowResults(true)}
          />
          {/* Search Results Dropdown */}
          {showResults && results.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-[var(--shadow-pop)] z-50 max-h-80 overflow-auto">
              {results.map((r, i) => {
                const Icon = typeIcon[r.type] || FileText;
                return (
                  <div key={i} className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 cursor-pointer transition-colors duration-100 border-b border-border last:border-0"
                    onClick={() => { navigate(r.url); setShowResults(false); setQuery(''); }}
                  >
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-muted shrink-0">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{r.title}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{r.subtitle}</p>
                    </div>
                    <Badge variant="outline" className="text-[9px] shrink-0 capitalize">{r.type}</Badge>
                  </div>
                );
              })}
            </div>
          )}
          {showResults && results.length === 0 && query.length >= 2 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-[var(--shadow-pop)] z-50 p-4 text-center">
              <p className="text-sm text-muted-foreground">No results for "{query}"</p>
            </div>
          )}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" data-testid="topbar-theme-toggle" onClick={toggleTheme} className="h-9 w-9">
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          
          {/* Alerts/Notifications Dropdown */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="topbar-notifications-button" className="h-9 w-9 relative">
                <Bell className="h-4 w-4" />
                {alerts.length > 0 && (
                  <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-destructive" />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
              <div className="p-4 border-b">
                <h4 className="font-semibold text-sm">Alerts</h4>
                <p className="text-xs text-muted-foreground">{alerts.length} active alert(s)</p>
              </div>
              <ScrollArea className="max-h-[400px]">
                {alerts.length > 0 ? (
                  <div className="p-2">
                    {alerts.map((alert, i) => (
                      <button
                        key={i}
                        onClick={() => navigate(`/vendors/${alert.vendor_id}`)}
                        className="w-full text-left p-3 rounded-lg hover:bg-muted/50 transition-colors duration-150 mb-1 flex items-start gap-3"
                      >
                        <div className={`mt-0.5 h-2 w-2 rounded-full shrink-0 ${alert.type === 'expired' ? 'bg-destructive' : 'bg-[hsl(36,90%,50%)]'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm">{alert.message}</p>
                          {alert.date && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {new Date(alert.date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <Bell className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-xs text-muted-foreground">No alerts</p>
                  </div>
                )}
              </ScrollArea>
            </PopoverContent>
          </Popover>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" data-testid="topbar-user-menu" className="h-9 gap-2 px-2">
                <Avatar className="h-7 w-7"><AvatarFallback className="bg-primary text-primary-foreground text-xs">{initials}</AvatarFallback></Avatar>
                <span className="text-sm font-medium hidden sm:inline">{user ? `${user.first_name} ${user.last_name}` : 'User'}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate('/profile')}><User className="mr-2 h-4 w-4" /> Profile</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')}><Settings className="mr-2 h-4 w-4" /> Settings</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout}><LogOut className="mr-2 h-4 w-4" /> Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
